import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const About = () => {
  const [team, setTeam] = useState([]);

  useEffect(() => {
    axios.get(`${API}/team`).then(res => setTeam(res.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <img
          src="/assets/images/vicenzo-livingrm.jpg"
          alt="Interior Design"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 text-center text-white">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-sm tracking-[0.3em] uppercase mb-4"
          >
            Our Story
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-5xl md:text-7xl font-serif"
          >
            Meet Charlene
          </motion.h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding bg-bone">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0 }}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <img
                src="/assets/images/charlene-about.webp"
                alt="Charlene Neal"
                className="w-full h-[600px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-coral text-white p-8 hidden md:block">
                <p className="text-xs tracking-widest uppercase">Founded</p>
                <p className="text-4xl font-serif">2007</p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0 }}
              transition={{ duration: 0.7 }}
            >
              <p className="text-sm tracking-[0.3em] uppercase text-bronze mb-4">President & Founder</p>
              <h2 className="text-4xl md:text-5xl font-serif mb-6">Charlene Neal</h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                With over 40 years of experience in home building, interior design, and model merchandising, Charlene Neal founded Pure Style in 2007 to bring personalized, full-service interior design to discerning clients throughout Southwest Florida.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                As Vice President of Design at Neal Communities and co-founder with her husband Pat, Charlene has collaborated on designing unique communities and over 20,000 homes in the region. Her passion lies in creating spaces that reflect the Florida lifestyle—filled with light, color, and energy.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-8">
                At Pure Style, we listen closely to our clients. Our mission is to understand your unique personality and needs, then create spaces that minimize stress and maximize beautiful results.
              </p>
              <div className="flex flex-wrap gap-8">
                <div>
                  <p className="text-3xl font-serif text-bronze">40+</p>
                  <p className="text-xs tracking-widest uppercase text-muted-foreground">Years Experience</p>
                </div>
                <div>
                  <p className="text-3xl font-serif text-bronze">20,000+</p>
                  <p className="text-xs tracking-widest uppercase text-muted-foreground">Homes Designed</p>
                </div>
                <div>
                  <p className="text-3xl font-serif text-bronze">45+</p>
                  <p className="text-xs tracking-widest uppercase text-muted-foreground">Years Partnership</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="section-padding bg-muted">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0 }}
              transition={{ duration: 0.7 }}
            >
              <p className="text-sm tracking-[0.3em] uppercase text-bronze mb-4">Our Philosophy</p>
              <h2 className="text-4xl md:text-5xl font-serif mb-8">Design That Reflects You</h2>
              <p className="text-xl font-serif text-muted-foreground leading-relaxed mb-6 italic">
                "Every home should tell the story of its inhabitants. Our role is simply to help you write that story beautifully."
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                At Pure Style, we believe that great design is personal. It's not about trends or what looks good in magazines—it's about creating a space that feels like home from the moment you walk through the door.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                We work closely with each client, taking the time to understand their lifestyle, preferences, and dreams. Then, we bring those visions to life using our extensive network of top industry resources, always balancing beauty with practicality and budget.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Meet the Team */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-custom">
          <div className="text-center mb-14">
            <p className="text-sm tracking-[0.3em] uppercase text-bronze mb-4">Our Team</p>
            <h2 className="text-3xl md:text-4xl font-serif mb-4">Meet the Pure Style Team</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Our talented team of designers and consultants brings decades of combined experience to every project.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {team.map((member, index) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                className="group"
              >
                <div className="relative overflow-hidden rounded-xl mb-4 aspect-[3/4]">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="font-serif text-lg">{member.name}</h3>
                <p className="text-sm text-bronze">{member.role}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/team"
              className="inline-flex items-center gap-2 text-bronze font-medium hover:gap-3 transition-all duration-300"
            >
              Learn More About Our Team
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Neal Communities Connection */}
      <section className="section-padding bg-bone">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0 }}
              transition={{ duration: 0.7 }}
            >
              <p className="text-sm tracking-[0.3em] uppercase text-bronze mb-4">Family of Companies</p>
              <h2 className="text-4xl md:text-5xl font-serif mb-6">
                Part of Something Greater
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Pure Style is proud to be part of the Neal Communities family of companies. This connection gives our clients access to comprehensive home solutions—from new construction to interior design—all under one trusted umbrella.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Our relationship with Neal Communities means we understand every aspect of home building, allowing us to provide seamless design services whether you're personalizing a new Neal home or transforming an existing residence.
              </p>
              <Link
                to="/contact"
                data-testid="about-contact-link"
                className="btn-primary inline-flex items-center gap-2"
              >
                Work With Us
                <ArrowRight size={16} />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0 }}
              transition={{ duration: 0.7 }}
            >
              <img
                src="/assets/images/vicenzo-pool.jpg"
                alt="Outdoor Living"
                className="w-full h-[500px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
